import * as _ from 'lodash';
import { Vehicle } from '../../shared/models';
import { Garage } from '../../shared/models/garage';
import { VehicleDeparture } from '../../shared/models/vehicle/vehicle-departure';
import { Deviation, VehicleDeviation } from '../../shared/models/vehicle/vehicle-deviation';
import { VehicleConfirmedWarning, VehicleWarning, VehicleWarningMessage, WarningMessageType } from '../../shared/models/vehicle/vehicle-warning';
import { TrafficDeviationReceipt, TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { EvSocPrognosisGarageSnapshot, EvSocPrognosisWarning, EvSocPrognosisVehicleSnapshot } from '../store/ev-soc-prognosis.store';

export class SignalRHubHelper {

    public static getVehiclesFromPositionMessage(data: any, groups: string[]) {
        const vehicles = _.chain(data)

            .filter((item) => _.includes(groups, item.grCode))
            .map((item) =>
                new Vehicle({
                    garageCode: item.grCode,
                    inventoryNumber: item.invNo,
                    position: item.lat === 0 && item.lon === 0
                        ? undefined : {
                            latitude: item.lat,
                            longitude: item.lon,
                            bearing: item.brg,
                            speed: item.sp,
                            timestamp: item.ts
                        }
                })
            )
            .filter((item: Vehicle) => item.position !== undefined)
            .value();

        return vehicles;
    }

    public static getVehiclesFromDeviationMessage(data: any, garages: Garage[], groups: string[]) {
        const debugLog = localStorage.getItem('debug.deviationLog') === 'true';
        const vehicles = _.chain(data)
            .filter((item) => this._isInGarageSelection(groups, item.garageCode))
            .map((item) => {
                if (debugLog) {
                    // Raw payload inspection: confirms which fields the backend actually populates.
                    console.log('[deviation msg]', {
                        invNo: item.inventoryNumber,
                        progressType: item.progressType,
                        stopName: item.stopName,
                        nextStopName: item.nextStopName,
                        sequenceNumber: item.sequenceNumber,
                        measuredAt: item.measuredAt,
                        measuredAt_typeof: typeof item.measuredAt,
                        routeNumber: item.routeNumber,
                        tripNumber: item.tripNumber,
                        keys: Object.keys(item),
                    });
                }
                let trafficAreaName: string;
                const garage = garages.find(g => g.code?.toLowerCase() === item.garageCode.toLowerCase());
                if (garage) {
                    trafficAreaName = garage.trafficAreaName;
                }

                const vehicle = new Vehicle({
                    garageCode: item.garageCode,
                    inventoryNumber: item.inventoryNumber,
                    // since the data back has empty string on block, route and trip and we want that data to be undefined in that case
                    blockNumber: item.blockNumber?.length ? item.blockNumber : undefined,
                    routeNumber: item.routeNumber?.length ? item.routeNumber : undefined,
                    tripNumber: item.tripNumber?.length ? item.tripNumber : undefined,
                    trafficAreaName,
                    progressType: item.progressType,
                    onVehicleTaskBlock: item.onVehicleTaskBlock,
                    onTrip: item.onTrip,
                    trafficDate: item.trafficDate
                });
                if (item.progressType === 'Departure') {
                    vehicle.departureDeviation = new VehicleDeviation({
                        stopName: item.stopName,
                        stopRef: item.stopRef,
                        nextStopName: item.nextStopName,
                        nextStopRef: item.nextStopRef,
                        sequenceNumber: item.sequenceNumber,
                        deviation: new Deviation(item.deviation),
                        measuredAt: item.measuredAt,
                        departures: _.map(item.departures, (d) => new VehicleDeparture(d)),
                        isTimingPoint: item.isTimingPoint
                    });
                } else if (item.progressType === 'Arrival') {
                    vehicle.arrivalDeviation = new VehicleDeviation({
                        stopName: item.stopName,
                        stopRef: item.stopRef,
                        nextStopName: item.nextStopName,
                        nextStopRef: item.nextStopRef,
                        sequenceNumber: item.sequenceNumber,
                        deviation: new Deviation(item.deviation),
                        measuredAt: item.measuredAt,
                        isTimingPoint: item.isTimingPoint
                    });
                }

                // Lilleström har problem med Telia RiS-modulen. Fordonen loggar inte ut
                // från omloppen och de får därför meddelanden med stora förseningar från fordon
                // som står på depån. Det här filtrerar bort de meddelandena. /Johan
                if ((vehicle.departureDeviation === undefined && vehicle.arrivalDeviation === undefined)
                    || vehicle.departureDeviation?.deviation?.getTotalSeconds() < -43200) { // 12 h
                    return undefined;
                } else {
                    return vehicle;
                }
            })
            .filter((vehicle) => vehicle !== undefined)
            .value();

        return vehicles;
    }

    public static getVehiclesFromWarningMessage(data: any, groups: string[],
        minuteFilterForFetchingWarningData: string, disabledWarningTypes: WarningMessageType[] = []) {
        const filterMinutes = parseInt(minuteFilterForFetchingWarningData, 10);
        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
            .filter((item) => this._isInGarageSelection(groups, item.garageCode))
            .filter((item) => item.measuredAt && new Date(item.measuredAt) > new Date(Date.now() - filterMinutes * 60000))
            // We now get Parking Warnings from CM. Can be removed whene it is removed from RealTime
            .filter(item => item.messageType !== WarningMessageType.NoParkingSpotFound)
            .filter(item => _.includes(disabledWarningTypes, item.messageType) === false) // filter away invalid warnings
            .groupBy((item) => item.inventoryNumber)
            .map((item) => {
                const firstItem = _.first(item);
                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.inventoryNumber,
                    warnings: _.map(item, i => this._mapWarning(i, false, true))
                });

                return vehicle;
            }).value();

        return vehicles;
    }

    public static getVehiclesFromParkingSpotWarningMessage(data: any, groups: number[], minuteFilterForFetchingWarningData: string) {
        const filterMinutes = parseInt(minuteFilterForFetchingWarningData, 10);
        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
            .filter((item) => _.includes(groups, item.garageId))
            .filter((item) => item.measuredAt && new Date(item.measuredAt) > new Date(Date.now() - filterMinutes * 60000))
            .groupBy((item) => item.vehicleInventoryNumber)
            .map((item) => {
                const firstItem = _.first(item);
                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.vehicleInventoryNumber,
                    warnings: _.map(item, i => this._mapParkingSpotWarning(i, true))
                });

                return vehicle;
            }).value();

        return vehicles;
    }

    public static getVehiclesFromLowSocWarningMessage(data: any, groups: number[], minuteFilterForFetchingWarningData: string) {
        const filterMinutes = parseInt(minuteFilterForFetchingWarningData, 10);
        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
            .filter((item) => _.includes(groups, item.garageId))
            .filter((item) => item.measuredAt && new Date(item.measuredAt) > new Date(Date.now() - filterMinutes * 60000))
            .groupBy((item) => item.vehicleInventoryNumber)
            .map((items) => {
                const firstItem = _.first(items);
                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.vehicleInventoryNumber,
                    warnings: _.map(items, i => this._mapLowSocWarning(i, true))
                });

                return vehicle;
            }).value();

        return vehicles;
    }


    public static getVehiclesFromWarningRestoredMessage(data: any, groups: string[], minuteFilterForFetchingWarningData: string,
        disabledWarningTypes: WarningMessageType[] = []
    ) {
        const filterMinutes = parseInt(minuteFilterForFetchingWarningData, 10);

        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
        .filter((item) => this._isInGarageSelection(groups, item.garageCode))
            // We now get Parking Warnings from CM. Can be removed whene it is removed from RealTime
            .filter(item => item.messageType !== WarningMessageType.NoParkingSpotFound)
            .filter(item => _.includes(disabledWarningTypes, item.messageType) === false) // filter away invalid warnings
            .groupBy((item) => item.inventoryNumber)
            .map((items) => {
                const firstItem = _.first(items);

                const warnings = _.map(items, item => {
                    const warning = this._mapWarning(item, false, false);
                    warning.measuredAt = item.measuredAt;
                    return warning;
                });

                const confirmedWarnings = _.chain(items)
                    .filter(item => item.confirmedAt)
                    .map(item => this._mapWarning(item, true, true))
                    .value();

                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.inventoryNumber,
                    warnings,
                    confirmedWarnings
                });

                return vehicle;

            }).value();

        return vehicles;
    }

    public static getVehiclesFromParkingSpotWarningRestoredMessage(data: any, groups: number[]) {

        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
            .filter((item) => _.includes(groups, item.garageId))
            .groupBy((item) => item.vehicleInventoryNumber)
            .map((items) => {
                const firstItem = _.first(items);

                const warnings = _.map(items, item => {
                    const warning = this._mapParkingSpotWarning(item, false);
                    warning.measuredAt = item.measuredAt;
                    return warning;
                });

                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.vehicleInventoryNumber,
                    warnings
                });

                return vehicle;

            }).value();

        return vehicles;
    }

    public static getVehiclesFromLowSocWarningRestoredMessage(data: any) {

        if (!_.isArray(data)) {
            data = [data];
        }

        const vehicles = _.chain(data)
            .groupBy((item) => item.vehicleInventoryNumber)
            .map((items) => {
                const firstItem = _.first(items);
                const warnings = _.map(items, item => {
                    const warning = this._mapLowSocWarning(item, false);
                    warning.measuredAt = item.measuredAt;
                    return warning;
                });

                const vehicle = new Vehicle({
                    garageCode: firstItem.garageCode,
                    inventoryNumber: firstItem.vehicleInventoryNumber,
                    warnings
                });

                return vehicle;

            }).value();

        return vehicles;
    }

    public static getDeviationsFromTrafficDeviationMessage(data: any, groups: string[]) {
        
        if (!_.isArray(data)) {
            data = [data];
        }

        const deviations = _.chain(data)
            .filter(item => this._isInGarageSelection(groups, item.garageCode))
            .map((item => ({ 
                ...item, 
                type: this._mapTrafficDeviationType(item.type), 
                trafficDeviationScope: JSON.parse(item.trafficDeviationScope), 
                receipt: {
                    ...(item.receipt ?? {}),
                    status: this.mapTrafficDeviationStatus(item.receipt?.status),
                }
            } as TrafficDeviationTypesUnion)))
            .value()

        return deviations;
    }

    public static getReceiptsFromTrafficDeviationReceiptMessage(data: any, groups: string[]) {
        
        if (!_.isArray(data)) {
            data = [data];
        }

        const receipts = _.chain(data)
            .filter(item => this._isInGarageSelection(groups, item.garageCode))
            .map((item => ({
                ...item,
                status: this.mapTrafficDeviationStatus(item.status),
            } as TrafficDeviationReceipt)))
            .value()

        return receipts;
    }


    private static _isInGarageSelection(groups: string[], garageCode: string): boolean {
        const lower = _.map(groups, g => g.toLowerCase());
        return _.includes(lower, garageCode.toLowerCase());
    }

    private static _mapTrafficDeviationType(type: number | string) {
        if(!Number.isInteger(type)) return type;

        switch (type) {
            case 1:
                return 'DeviationInformation';
            case 2:
                return 'ChangeOfJourneyPattern';
            case 3:
                return 'ChangeOfJourneyTiming';
            case 4:
                return 'PredictionInaccurate';
            case 5:
                return 'ConnectionCancellation';
            default:
                return 'Unknown';
        }
    }

    public static mapTrafficDeviationStatus(status: number | string) {
        if(Number.isInteger(status)) return status;

        switch (status) {
            case 'SentOk':
                return 2;
            case 'ProcessedOk':
                return 3;
            case 'SentError':
                return 4;
            case 'ProcessedErrorServer':
                return 5;
            case 'ProcessedErrorData':
                return 6;
            case 'Error':
                return 7;
            default:
                return 0;
        }
    }

    private static _mapWarning(item: any, confirmed: boolean, isActive: boolean): VehicleWarning {
        const warningMessages = _.map(item.translatedMessages || [], val =>
            new VehicleWarningMessage({ message: val.text, culture: val.culture }));

        const warning = {
            isActive,
            measuredAt: item.timeStamp ?? item.measuredAt,
            severityRatio: item.severityRatio,
            messageType: item.messageType,
            warningMessages,
            metadata: item.metadata
        };

        if (confirmed) {
            Object.assign(warning, {
                confirmedAt: item.confirmedAt,
                comment: item.comment,
                confirmedBy: item.confirmedBy
            });

            return new VehicleConfirmedWarning(warning);
        }

        return new VehicleWarning(warning);
    }

    private static _mapParkingSpotWarning(item: any, isActive: boolean): VehicleWarning {

        const warning = {
            isActive,
            measuredAt: item.timeStamp ?? item.measuredAt,
            severityRatio: item.severityRatio,
            messageType: WarningMessageType.NoParkingSpotFound,
            metadata: { startTime: item.startTime }
        };

        return new VehicleWarning(warning);
    }

    private static _mapLowSocWarning(item: any, isActive: boolean): VehicleWarning {

        const warning = {
            isActive,
            measuredAt: item.measuredAt,
            severityRatio: item.severityRatio,
            messageType: WarningMessageType.SocWarning,
            metadata: {sensorValue : item.sensorValue, planningLimitSoC : item.planningLimitSoC  }
        };

        return new VehicleWarning(warning);
    }

    public static mapLowSocPrognosisWarningMessage(
        data: any,
        groups: number[]
    ): EvSocPrognosisGarageSnapshot | undefined {
        const payload = Array.isArray(data) ? data[0] : data;
        const garageId = Number(payload?.garageId);

        if (!Number.isFinite(garageId) || !_.includes(groups, garageId)) {
            return undefined;
        }

        const vehicleWarnings = payload?.warnings;
        if (!_.isArray(vehicleWarnings)) {
            return { garageId, vehicles: [] };
        }

        const vehicles: EvSocPrognosisVehicleSnapshot[] = [];
        const maxStartTime = Date.now() + 10 * 60 * 60 * 1000; // 10 hours from now

        for (const vehicleEntry of vehicleWarnings) {
            const inventoryNumber = vehicleEntry.inventoryNumber;
            if (!inventoryNumber) {
                continue;
            }

            const warnings = vehicleEntry.warnings;
            if (!_.isArray(warnings) || warnings.length === 0) {
                continue;
            }

            const mappedWarnings = warnings
                .map((warning): EvSocPrognosisWarning => ({
                    startTime: warning.startTime ? new Date(warning.startTime) : undefined,
                    depotTime: new Date(warning.depotTime),
                    stateOfChargeAtDepot: warning.stateOfChargeAtDepot,
                }))
                .filter(warning => warning.startTime == null || warning.startTime.getTime() <= maxStartTime);

            if (mappedWarnings.length === 0) {
                continue;
            }

            vehicles.push({ inventoryNumber, warnings: mappedWarnings });
        }

        return { garageId, vehicles };
    }
}
